import { useEffect, useRef } from "react";

// Import league logos
import botola from "../assets/cover/league/Logo_Botola_12.2019.jpg";
import premierLeague from "../assets/cover/league/Premier_League_Logo.svg.png";
import ufc from "../assets/cover/league/UFC_Logo.png";
import belgianPro from "../assets/cover/league/belgian_pro_league-logo_brandlogos.net_gej8f.png";
import cff from "../assets/cover/league/cff.png";
import hburt from "../assets/cover/league/hburt.webp";
import hg1 from "../assets/cover/league/hg-1-scaled.png";
import hh from "../assets/cover/league/hh.png";
import hhbnnjh from "../assets/cover/league/hhbnnjh.png";
import oikj from "../assets/cover/league/oikj-scaled.png";
import rsl from "../assets/cover/league/rsl.png";
import serieA from "../assets/cover/league/serie_a-logo_brandlogos.net_kmz4e.png";
import tz1 from "../assets/cover/league/tz-1.png";
import u from "../assets/cover/league/u.png";
import uhkojlk from "../assets/cover/league/uhkojlk.png";
import zhg from "../assets/cover/league/zhg.png";
import zhjik from "../assets/cover/league/zhjik-scaled.png";
import mls from "../assets/cover/league/Major.webp";

// Dynamically import all sports posters in assets/cover/sports
const sportsContext = require.context("../assets/cover/sports", false, /\.(png|jpe?g|svg|webp)$/);
const sportsImages = sportsContext.keys().map((key) => sportsContext(key));

export default function Sports() {
  const carouselRef = useRef(null);
  const scrollInterval = useRef(null);

  const blockUserScroll = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const leagueLogos = [
    { name: "ZHJ", src: zhjik },
    { name: "U", src: u },
    { name: "Jupiler Pro League", src: belgianPro },
    { name: "Premier League", src: premierLeague },
    { name: "OIKJ", src: oikj },
    { name: "Serie A", src: serieA },
    { name: "Saudi Pro League", src: rsl },
    { name: "Botola Pro", src: botola },
    { name: "UFC", src: ufc },
    { name: "CAF", src: cff },
    { name: "HB", src: hburt },
    { name: "HG1", src: hg1 },
    { name: "HH", src: hh },
    { name: "HHBN", src: hhbnnjh },
    { name: "TZ1", src: tz1 },
    { name: "UHK", src: uhkojlk },
    { name: "ZHG", src: zhg },
    { name: "MLS", src: mls },
  ];

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const startInfiniteScroll = () => {
      scrollInterval.current = setInterval(() => {
        const singleSetWidth = carousel.scrollWidth / 3;
        const currentScroll = carousel.scrollLeft;
        
        if (currentScroll >= singleSetWidth) {
          carousel.scrollLeft = 0;
        } else {
          carousel.scrollLeft += 1;
        }
      }, 20);
    };

    const initializeCarousel = () => {
      const images = carousel.querySelectorAll('img');
      const loadedImages = Array.from(images).filter(img => img.complete);
      
      if (loadedImages.length === images.length || loadedImages.length > 0) {
        startInfiniteScroll();
      } else {
        setTimeout(() => {
          startInfiniteScroll();
        }, 500);
      }
    };

    setTimeout(initializeCarousel, 100);

    return () => {
      if (scrollInterval.current) {
        clearInterval(scrollInterval.current);
      }
    };
  }, []);

  return (
    <>
      <section className="py-16 bg-slate-50">
        <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto">
          <div className="text-center mb-12">
            <h1 className="text-2xl md:text-3xl lg:text-4xl mx-auto font-black bg-clip-text bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] mb-4 max-w-3xl leading-snug text-center">
              Live Sport &amp; Premium Sportcontent
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ontdek alle sportkanalen en je favoriete wedstrijden in één abonnement
            </p>
          </div>

          {/* Infinite Carousel */}
          <div className="relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-transparent z-10"></div>
            <div
              ref={carouselRef}
              className="flex space-x-4 overflow-hidden carousel-container"
              style={{
                scrollBehavior: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                touchAction: "none",
                overscrollBehaviorX: "contain",
              }}
              onWheel={blockUserScroll}
              onTouchStart={blockUserScroll}
              onTouchMove={blockUserScroll}
              onPointerDown={blockUserScroll}
              onMouseDown={blockUserScroll}
            >
              {[...sportsImages, ...sportsImages, ...sportsImages].map(
                (image, index) => {
                  return (
                    <div
                      key={`${index}-${Math.random()}`}
                      className="flex-shrink-0 group relative"
                    >
                      <div className="relative w-48 h-80 md:w-52 md:h-[22rem] rounded-2xl overflow-hidden transition-all duration-500 shadow-md mb-2">
                        <img
                          src={image}
                          alt={`Sport ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-500 carousel-image"
                          draggable={false}
                          onDragStart={(e) => e.preventDefault()}
                        />
                      </div>
                    </div>
                  );
                }
              )}
            </div>
            {/* Edge fade overlays */}
            <div className="carousel-edge-left"></div>
            <div className="carousel-edge-right"></div>
          </div>

          {/* League Logos Grid */}
          <div className="mt-5">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {leagueLogos.map((logo, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-110 border border-gray-100"
                >
                  <div className="flex items-center justify-center h-8">
                    <img
                      src={logo.src}
                      alt={logo.name}
                      className="max-h-14 max-w-full object-contain filter group-hover:brightness-110 transition-all duration-300"
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
